import SmoothScroll from "./Utils/SmoothScroll";
import Scroll from "./Scroll";
import Loader from "./Utils/Loader";
import Events from "./Utils/Events";
import { config, defaultAssets, experienceAssets } from "../config";
import VideoCarousel from "./VideoCarousel";
import StateManager from "./StateManager";
import ContentUpdater from "./ContentUpdater";
import ShareGenerator from "./ShareGenerator";
import Modal from "./Modal";
import gsap from "gsap";
import observer from "./Utils/IntersectionObserver";

/* 
  This class handles the main experience logic, including loading assets,
  managing state, and controlling the video carousel and modals.
  It uses the Singleton pattern to ensure only one instance of Experience exists.
*/

// Singleton pattern to ensure only one instance of Experience exists
let instance = null;

class Experience {
  constructor(params = {}, missingParams = false) {
    if (instance) return instance; // Return existing instance if already created
    instance = this; // Set the singleton instance

    this.params = params;
    this.missingParams = missingParams;

    // Initialize components
    this.stateManager = new StateManager(this.params);
    this.state = this.stateManager.state;
    this.loader = this.initializeLoader();
    this.introVideoElement = document.querySelector(".intro");
    this.introVideo = this.introVideoElement.querySelector("video");
    this.smoothScroll = new SmoothScroll();
    this.scroll = new Scroll();
    this.artworkTargets = this.getArtworkTargets();
    this.personaTargets = this.getPersonaTargets();
    this.shareTargets = Array.from(document.querySelectorAll("[data-share]"));
    this.contentUpdater = this.initializeContentUpdater();
    this.videoCarousel = new VideoCarousel();
    this.modal = new Modal();

    this.muteButton = document.querySelector(".mute__button");
    this.backgroundAudio = document.querySelector("audio");
    this.isFading = false; // Flag to track if fade is active

    this.backgroundAudio.muted = true;

    // Initialize the social share generator
    if (!this.missingParams) {
      this.share = new ShareGenerator(config.share, this.state);
    }

    this.artworkSection = document.querySelector(".artwork");
    this.artworkDownloadButtons = this.artworkSection.querySelectorAll(
      "[data-share='download']"
    );

    // Add event listeners for the download buttons
    this.artworkDownloadButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        const tooltip = button.nextElementSibling;
        tooltip.classList.add("active");

        setTimeout(() => {
          tooltip.classList.remove("active");
        }, 8000);
      });
    });

    // Set up event listeners
    this.setupEventListeners();

    // Start asset preloading
    this.loader.preload();

    // Add animate class to elements in view
    this.artworkTextContainers = document.querySelectorAll(".artwork__text");
    this.artworkTextContainers.forEach((container) => {
      observer.observe(container);
    });
  }

  toggleAudio() {
    // Update the mute button class based on mute state
    const isMuted = !this.backgroundAudio.muted;

    // Toggle the class based on the new mute state
    this.muteButton.classList.toggle("active", !isMuted);

    if (!isMuted) {
      // Fade in the audio if unmuted
      this.fadeAudio(this.backgroundAudio, 0, 0.1, 2000, () => {
        this.isFading = false; // Reset flag after fade completes
      });
    } else {
      // Fade out the audio and mute it after the fade-out completes
      this.fadeAudio(this.backgroundAudio, 0.1, 0, 1000, () => {
        this.backgroundAudio.muted = true;
        this.isFading = false; // Reset flag after fade completes
      });
    }
  }

  fadeAudio(audioElement, startVolume, endVolume, duration, callback) {
    // Detect iOS Safari user agent
    const isIOS =
      (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) &&
      /Safari/i.test(navigator.userAgent) &&
      !/Chrome/i.test(navigator.userAgent);

    if (isIOS) {
      // console.log("iOS Safari detected, skipping fade.");

      // Directly mute/unmute the audio without fading for iOS Safari
      if (endVolume === 0) {
        audioElement.muted = true;
        audioElement.pause();
        // console.log("Audio paused and muted for iOS Safari.");
      } else {
        audioElement.muted = false;
        audioElement.play();
        // console.log("Audio unmuted and playing for iOS Safari.");
      }

      if (callback) callback(); // Execute callback if provided
      return;
    }

    // Non-iOS browsers: Proceed with fade logic
    if (this.isFading) {
      // console.log("Fade is already in progress. Ignoring new request.");
      return; // Prevent multiple fades
    }
    this.isFading = true; // Mark fading as active
    // console.log(
    //   `Starting fade: from ${startVolume} to ${endVolume} over ${duration}ms`
    // );

    audioElement.volume = startVolume;

    if (startVolume === 0) {
      audioElement.play();
      audioElement.muted = false; // Unmute for fade-in
      // console.log("Audio unmuted and playing.");
    }

    const volumeStep = 0.01;
    const fadeInterval =
      Math.abs(duration / (endVolume - startVolume)) * volumeStep;

    const fade = setInterval(() => {
      // console.log(`Current volume: ${audioElement.volume}`);

      if (startVolume < endVolume) {
        // Fade in logic: increment volume
        audioElement.volume = Math.min(
          audioElement.volume + volumeStep,
          endVolume
        );
        // console.log(`Fading in. Volume increased to: ${audioElement.volume}`);
      } else if (startVolume > endVolume) {
        // Fade out logic: decrement volume
        audioElement.volume = Math.max(
          audioElement.volume - volumeStep,
          endVolume
        );
        // console.log(`Fading out. Volume decreased to: ${audioElement.volume}`);
      }

      // Stop the fade when the target volume is reached
      if (audioElement.volume === endVolume) {
        clearInterval(fade);
        // console.log("Fade complete.");

        if (endVolume === 0) {
          audioElement.pause(); // Fallback: Pause the audio if faded out to 0
          audioElement.muted = true; // Explicitly mute after fading out
          // console.log("Audio paused and muted.");
        }

        this.isFading = false; // Mark fading as complete
        // console.log("isFading flag reset.");

        if (callback) {
          callback(); // Execute callback if provided
        }
      }
    }, fadeInterval);

    // console.log(`Fade interval set with step interval: ${fadeInterval}`);
  }

  // Returns the list of assets to load based on the state and experience configuration
  getAssetsToLoad() {
    return [
      ...this.stateManager.getAssetsFromState(), // Get assets from the current state
      ...defaultAssets, // Add default assets
      ...experienceAssets, // Add general experience assets
    ];
  }

  // Initializes the loader with the necessary assets
  initializeLoader() {
    const assetsToLoad = this.missingParams
      ? defaultAssets // If missing params, only load default assets
      : this.getAssetsToLoad(); // Otherwise, load all required assets
    return new Loader(assetsToLoad);
  }

  // Returns all artwork elements from the DOM
  getArtworkTargets() {
    return Array.from(document.querySelectorAll("[data-artwork]"));
  }

  // Returns all persona elements from the DOM
  getPersonaTargets() {
    return Array.from(document.querySelectorAll("[data-persona]"));
  }

  // Initializes ContentUpdater to manage content updates
  initializeContentUpdater() {
    return new ContentUpdater(this.state, this.loader, {
      artworkTargets: this.artworkTargets,
      personaTargets: this.personaTargets,
    });
  }

  // Sets up event listeners for asset loading and window resize
  setupEventListeners() {
    Events.subscribe("assets.loaded", this.onAssetsLoaded.bind(this));

    this.muteButton.addEventListener("click", () => this.toggleAudio());

    // Add a listener for window resize events to handle scroll resizing
    window.addEventListener("resize", this.onResize.bind(this));
    this.smoothScroll.on("scroll", this.onScroll.bind(this));
  }

  // Handles the window resize event by calling the Scroll's resize handler
  onResize() {
    this.scroll.onResize();
  }

  // Handles the smooth scroll event
  onScroll(event) {
    // this.scroll.onScroll(event);
  }

  // Triggered when assets are fully loaded
  onAssetsLoaded() {
    this.playIntroVideo();
    // Handle social share integration after assets have loaded
    if (!this.missingParams) {
      // Track which types have been loaded to reuse URLs
      // This is because there are duplicate buttons
      let blobs = {};
      // Iterate over each button
      this.shareTargets.forEach(async (element, index) => {
        // HACK: Something causes the wrong images to render
        // Using a timeout for now to work around this
        window.setTimeout(async () => {
          // const type = element.dataset.type;
          // if (element.dataset.share === "download") {
          //   if (!blobs[type]) {
          //     // renders image on load to be opened in new tab. Fix for Safari
          //     // blobs[type] = await this.share.download(type);
          //   }
          //   element.dataset.href = blobs[type];
          // }
          element.addEventListener("click", (event) => {
            event.preventDefault();
            switch (element.dataset.share) {
              case "download":
                // window.open(element.dataset.href, "_blank");
                // This causes popup blocker issues in Safari
                this.share.download(element.dataset.type);
                break;
              case "facebook":
              case "twitter":
                this.share.share(element.dataset.share, element.dataset.type);
                break;
            }
          });
        }, 100 * index);
      });
    }
  }

  // Plays the intro video and sets up the fade-out behavior
  async playIntroVideo() {
    // Add the timeupdate event listener for the fade-out
    const handleTimeUpdate = () => {
      if (this.introVideo.currentTime >= this.introVideo.duration - 1.2) {
        this.fadeOutIntroVideo(handleTimeUpdate);
      }
    };

    // Handle exceptions for playing video
    // Safari can block the playing of the video, throwing an exception
    try {
      // Await the promise to ensure exceptions can be caught
      await this.introVideo.play();
      // Then handle the time update listener
      this.introVideo.addEventListener("timeupdate", handleTimeUpdate);
    } catch (exception) {
      // Otherwise fade out the video if it won't play
      this.fadeOutIntroVideo();
    }
  }

  // Fades out the intro video and shows the content
  fadeOutIntroVideo(handleTimeUpdate) {
    // console.log("Intro video ending soon, fading out...");
    gsap.to(this.introVideo, {
      duration: 1,
      autoAlpha: 0,
      onComplete: () => {
        this.introVideoElement.style.display = "none";
        this.showContent();
        Events.emit("intro.ended");
      },
    });

    // Remove the timeupdate event listener to prevent it from running again
    if (handleTimeUpdate) {
      this.introVideo.removeEventListener("timeupdate", handleTimeUpdate);
    }
  }

  // Decides whether to show fallback or experience content based on params
  showContent() {
    if (this.missingParams) {
      this.showFallbackContent();
    } else {
      this.showExperienceContent();
    }
  }

  // Shows fallback content when required params are missing
  showFallbackContent() {
    console.log("Showing fallback content with default assets");
    this.modal.setFallback(); // Set the modal to display fallback content
  }

  // Displays the main experience content based on params and default assets
  showExperienceContent() {
    console.log(
      "Showing experience content based on params and default assets"
    );
    this.contentUpdater.updateContent(this.videoCarousel);
    this.modal.show("legal"); // Optionally show the legal modal
  }
}

export default Experience;
